import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/lib/ThemeContext";
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import HistoryPage from "@/pages/HistoryPage";
import AdminPage from "@/pages/AdminPage";
import OnboardingPage from "@/pages/OnboardingPage";
import { TermsPage, PrivacyPage, DataDeletionPage } from "@/pages/LegalPages";
import { apiCall } from "@/lib/api";
import ApiDocsPage from "@/pages/ApiDocsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "/veritel-logo.jpeg";

// Dynamic meta tags (Emergent platform overrides index.html)
const usePageMeta = (title, description) => {
  useEffect(() => {
    document.title = title || "Veritel - Verificacion de Contactos";
    const setMeta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const desc = description || "Veritel - Verificacion inteligente de contactos. Valida telefonos, emails y WhatsApp de forma masiva.";
    setMeta("description", desc);
    setMeta("og:title", "Veritel - Verificacion de Contactos", "property");
    setMeta("og:description", desc, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:image", LOGO_URL, "property");
    setMeta("twitter:card", "summary");
    setMeta("twitter:title", "Veritel - Verificacion de Contactos");
    // Favicon
    let link = document.querySelector("link[rel='icon']");
    if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
    link.type = "image/png"; link.href = LOGO_URL;
    // Remove Emergent badge
    const removeBadge = () => { const b = document.getElementById("emergent-badge"); if (b) b.remove(); };
    removeBadge();
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [title, description]);
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [authStatus, setAuthStatus] = useState("Autenticando...");
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Try to use an existing session cookie first. If /auth/me succeeds we skip the
    // OAuth exchange entirely - this avoids the "Autenticacion fallida" error when
    // the user reloads (F5) or reopens a tab whose URL still contains a one-shot
    // session_id in the hash.
    const tryExistingSession = async () => {
      try {
        const r = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (r.ok) return await r.json();
      } catch (_) { /* fall through */ }
      return null;
    };

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");

      // 1) Si ya hay sesion valida, limpiamos el hash y vamos al dashboard directo
      const existing = await tryExistingSession();
      if (existing) {
        window.history.replaceState(null, '', window.location.pathname);
        if (existing.needs_onboarding) {
          navigate("/onboarding", { replace: true, state: { user: existing } });
        } else {
          navigate("/dashboard", { replace: true, state: { user: existing } });
        }
        return;
      }

      // 2) Si no hay sesion, necesitamos el session_id del hash
      if (!sessionId) {
        setAuthStatus("Sesion expirada. Vuelve a iniciar sesion.");
        setHasError(true);
        setTimeout(() => navigate("/", { replace: true }), 2000);
        return;
      }

      setAuthStatus("Validando sesion...");
      try {
        const { ok, data } = await apiCall("POST", `${API}/auth/session`, { session_id: sessionId });
        if (ok) {
          window.history.replaceState(null, '', window.location.pathname);
          if (data.needs_onboarding) {
            navigate("/onboarding", { replace: true, state: { user: data } });
          } else {
            navigate("/dashboard", { replace: true, state: { user: data } });
          }
        } else {
          // El session_id expiro o ya fue usado. Como quizas la cookie es valida
          // (race), reintentamos /auth/me una ultima vez antes de mandar a login.
          const retry = await tryExistingSession();
          if (retry) {
            window.history.replaceState(null, '', window.location.pathname);
            navigate(retry.needs_onboarding ? "/onboarding" : "/dashboard",
              { replace: true, state: { user: retry } });
            return;
          }
          setAuthStatus(`${data.detail || "Sesion expirada"} - redirigiendo...`);
          setHasError(true);
          window.history.replaceState(null, '', window.location.pathname);
          setTimeout(() => navigate("/", { replace: true }), 2500);
        }
      } catch (error) {
        console.error("Auth error:", error);
        window.history.replaceState(null, '', '/');
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--app-bg)" }}>
      <div className="text-center">
        {!hasError && (
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        )}
        <p className="t-text-secondary" data-testid="auth-status">{authStatus}</p>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (location.state?.user) {
      setIsAuthenticated(true);
      setUser(location.state.user);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: "include",
        });

        if (response.ok) {
          const userData = await response.json();
          setIsAuthenticated(true);
          setUser(userData);
        } else {
          setIsAuthenticated(false);
          navigate("/", { replace: true });
        }
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/", { replace: true });
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--app-bg)" }}>
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children({ user, setUser });
};

function AppRouter() {
  const location = useLocation();
  usePageMeta();

  // Check for session_id in URL hash SYNCHRONOUSLY before rendering routes
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => <Dashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            {({ user }) => {
              if (user?.role !== "superadmin") {
                return (
                  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--app-bg)", color: "var(--app-text)" }}>
                    <div className="text-center space-y-4">
                      <h2 className="text-xl font-bold text-rose-400">Acceso denegado</h2>
                      <p className="t-text-secondary">Solo el superadministrador puede acceder al panel de administracion.</p>
                    </div>
                  </div>
                );
              }
              return <AdminPage user={user} />;
            }}
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            {({ user }) => <HistoryPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/api-docs"
        element={
          <ProtectedRoute>
            {() => <ApiDocsPage />}
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App min-h-screen" style={{ backgroundColor: "var(--app-bg)" }}>
        <BrowserRouter>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
